import {
  DocumentData,
  DocumentSnapshot,
  FirestoreDataConverter,
  PartialWithFieldValue,
  SnapshotOptions,
  Timestamp,
  WithFieldValue,
} from "firebase/firestore";
import type { TowCompanyLocation, VendorLocation } from "../locations/locations";
import type { RepairShopGrade } from "../transporter/transporter";

const maybeTimestampToDate = (value: unknown): unknown => {
  if (value instanceof Timestamp) return value.toDate();
  return value;
};

export const vendorConverter = {
  toFirestore(
    modelObject: WithFieldValue<VendorLocation> | PartialWithFieldValue<VendorLocation>,
  ): DocumentData {
    const { id: _omitId, ...rest } = modelObject as Record<string, unknown>;
    return Object.fromEntries(
      Object.entries(rest).filter(([, v]) => v !== undefined),
    ) as DocumentData;
  },
  fromFirestore(snapshot: DocumentSnapshot, options: SnapshotOptions): VendorLocation {
    const data = snapshot.data(options);
    if (!data) {
      return {
        id: snapshot.id,
        name: "",
        address: "",
        city: "",
        state: "",
        zip: "",
      };
    }
    return {
      id: snapshot.id,
      name: typeof data.name === "string" ? data.name : "",
      address: typeof data.address === "string" ? data.address : "",
      city: typeof data.city === "string" ? data.city : "",
      state: typeof data.state === "string" ? data.state : "",
      zip: typeof data.zip === "string" ? data.zip : "",
      contact_name: data.contact_name,
      contact_phone: data.contact_phone,
      notes: data.notes,
      created_at: maybeTimestampToDate(data.created_at),
      updated_at: maybeTimestampToDate(data.updated_at),
    };
  },
} as FirestoreDataConverter<VendorLocation>;

const parseGrade = (value: unknown): RepairShopGrade | undefined => {
  if (value === "A" || value === "B" || value === "C") return value;
  return undefined;
};

const parseOptionalNumber = (value: unknown): number | undefined =>
  typeof value === "number" && Number.isFinite(value) ? value : undefined;

export const towCompanyConverter = {
  toFirestore(
    modelObject: WithFieldValue<TowCompanyLocation> | PartialWithFieldValue<TowCompanyLocation>,
  ): DocumentData {
    const { id: _omitId, ...rest } = modelObject as Record<string, unknown>;
    return Object.fromEntries(
      Object.entries(rest).filter(([, v]) => v !== undefined),
    ) as DocumentData;
  },
  fromFirestore(snapshot: DocumentSnapshot, options: SnapshotOptions): TowCompanyLocation {
    const data = snapshot.data(options);
    if (!data) {
      return {
        id: snapshot.id,
        name: "",
        address: "",
        city: "",
        state: "",
        zip: "",
      };
    }
    return {
      id: snapshot.id,
      name: typeof data.name === "string" ? data.name : "",
      address: typeof data.address === "string" ? data.address : "",
      city: typeof data.city === "string" ? data.city : "",
      state: typeof data.state === "string" ? data.state : "",
      zip: typeof data.zip === "string" ? data.zip : "",
      contact_name: data.contact_name,
      contact_phone: data.contact_phone,
      grade: parseGrade(data.grade),
      communication: parseOptionalNumber(data.communication),
      price: parseOptionalNumber(data.price),
      speed: parseOptionalNumber(data.speed),
      towing_radius_miles: parseOptionalNumber(data.towing_radius_miles),
      notes: data.notes,
      created_at: maybeTimestampToDate(data.created_at),
      updated_at: maybeTimestampToDate(data.updated_at),
    };
  },
} as FirestoreDataConverter<TowCompanyLocation>;
